import { useMutation, UseMutationOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { staticSuppliers } from "../config/constants";
import { IngredientSource } from "../config/enums/IngredientType.enum";
import { errorView, successMessage } from "../helpers/ErrorHandler";
import { ApiProductReviewResponse } from "../types/prodcut-data-review/product-data.types";
import { ApiServiceErr, axiosApi, MutOptions } from "./apiService";

interface IngredientImageData {
  ingredient_id: string;
  file: any;
}

interface IngredientImageId {
  ingredient_id: string;
}

interface CheckDrawingData {
  ingredientId: string;
  supplierId: string;
}

interface SupplierIngredientsParams {
  sourceId: string;
  ownerId: string;
}

interface SupportingDocumentParams {
  ingredientId: string;
  supplierId: string;
  file: File;
}

export const useGetIngredientList = (
  pageNumber: number,
  pageLimit: number,
  searchKey: string,
  filters: any
) =>
  useQuery(
    ["useGetIngredientList", pageNumber, pageLimit, searchKey, filters],
    async () => {
      const response = await axiosApi.post("/ingredient/list", {
        pageNumber,
        pageSize: pageLimit,
        searchKey,
        ...filters
      });
      return response.data;
    },
    { refetchOnWindowFocus: false }
  );

export const useGetIngredientWholeList = (
  addNew?: any,
  ingredientSource: any = IngredientSource.BILL_OF_MATERIAL
) =>
  useQuery(["getIngredientWholeList", addNew, ingredientSource], async () => {
    const response = await axiosApi.get(`/ingredient/list?ingredientSource=${ingredientSource}`);
    const newD = await response?.data?.map((ingred: any) => {
      const newIngredient: any = {
        id: ingred._id,
        title: ingred.ingredient_name,
        ingredientData: ingred
      };
      return newIngredient;
    });
    if (addNew) {
      return [{ id: 0, title: "+ New" }, ...newD];
    } else {
      return newD;
    }
  });

export const useGetCalculatedIngredientsWithSuppliers = () =>
  useQuery(
    ["getCalculatedIngredientsWithSuppliers"],
    async () => {
      const response = await axiosApi.get(`/ingredient/calculated-ingredients`);
      if (response.status === 200) {
        const ingredients = response.data.map((ingred: any) => ({
          key: ingred.ingredientId,
          text: ingred.ingredientName,
          value: ingred.ingredientId,
          suppliers: ingred.suppliers.map((supp: any) => ({
            key: `${ingred.ingredientId}-${supp.supplierId}`,
            text: supp.supplierName,
            value: supp.supplierId
          }))
        }));
        return ingredients;
      }
      return [];
    },
    {
      refetchOnWindowFocus: false
    }
  );

export const useGetIngredientsWithSupplier = () =>
  useQuery(
    ["getIngredientsWithSupplier"],
    async () => {
      const response = await axiosApi.get(`/ingredient/list`);
      if (response.status === 200) {
        const newD = await response?.data?.map((ingred: any) => {
          const newDSupp = ingred?.suppliers?.map((supp: any, index: any) => {
            return {
              key: `${ingred._id}-${supp._id}`,
              text: `${ingred.ingredient_name}${
                supp?.supplier_name ? `, ${supp?.supplier_name || ""}` : ""
              }`,
              value: {
                ingredient_id: ingred._id,
                ingredient_name: ingred.ingredient_name,
                supplier_id: supp._id,
                supplier_name: supp.supplier_name
              }
            };
          });
          return newDSupp;
        });

        return [...newD.flat()];
      } else {
        return response.data;
      }
    },
    {
      refetchOnWindowFocus: false
    }
  );

export const useGetSupplierIngredientDetail = (supplierId: string, ingredientId: string) =>
  useQuery<any, ApiServiceErr>(
    ["supplierIngredientDetail", supplierId, ingredientId],
    async () => {
      const response = await axiosApi.get(`/supplier/${supplierId}/ingredient/${ingredientId}`);
      return response.data;
    },
    {
      enabled: !!supplierId && !!ingredientId
    }
  );

export const usePutIngredientDataToQueue = (
  opt?: UseMutationOptions<string, ApiServiceErr, { ingredientId: string; supplierId: string }, any>
) =>
  useMutation<string, ApiServiceErr, { ingredientId: string; supplierId: string }>(
    async ({ ingredientId, supplierId }) => {
      const response = await axiosApi.put(`/ingredient/queue/${ingredientId}/${supplierId}`);
      successMessage(response.data);
      return response.data;
    },
    opt
  );

export const useGetIngredientsProductList = () =>
  useQuery(
    ["getIngredientProductList"],
    async () => {
      const response = await axiosApi.get(
        "/products?product_status=ACTIVE&tenant_id=null&isAllData=true"
      );

      if (response.status === 200) {
        const newD = await response?.data?.result?.map((product: any) => {
          const newProduct: any = {
            id: product._id,
            title: product.product_name,
            product_type: product.product_type,
            productDetails: {
              product_id: product._id,
              product_name: product.product_name,
              tenant_id: product.tenantId
            }
          };
          return newProduct;
        });
        return newD;
      } else {
        return response.data;
      }
    },
    {
      refetchOnWindowFocus: false
    }
  );

export const useGetIngredientsSupplierList = (addNew: boolean = true) =>
  useQuery(
    ["getIngredientSupplierList", addNew],
    async () => {
      const response = await axiosApi.get("/supplier/list");

      if (response.status === 200) {
        const newD = response.data.map((supplier: any) => ({
          id: supplier._id,
          title: supplier?.supplier_name,
          supplierDetails: supplier
        }));
        return addNew ? [...staticSuppliers, ...newD] : newD;
      }

      return response.data;
    },
    {
      refetchOnWindowFocus: false
    }
  );

export const usePostIngredientData = (data?: any) => {
  const queryClient = useQueryClient();

  return useMutation<any, ApiServiceErr, any>(
    async data => {
      const response = await axiosApi.post(`ingredient`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        // Invalidate specific queries or query keys after a successful mutation
        queryClient.invalidateQueries(["getIngredientList"]);
      }
    }
  );
};

export const usePutIngredientData = () => {
  const queryClient = useQueryClient();
  return useMutation<string, ApiServiceErr, any>(
    async data => {
      const response = await axiosApi.put(`ingredient/${data?._id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["getIngredientList"]);
      }
    }
  );
};

export const usePostSupplierData = (data?: any) => {
  const queryClient = useQueryClient();

  return useMutation<string, ApiServiceErr, any>(
    async data => {
      const response = await axiosApi.post(`supplier`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        // Invalidate specific queries or query keys after a successful mutation
        queryClient.invalidateQueries(["getIngredientList"]);
      }
    }
  );
};

export const usePutSupplierData = (data?: any) => {
  const queryClient = useQueryClient();

  return useMutation<string, ApiServiceErr, any>(
    async data => {
      const response = await axiosApi.put(`supplier/${data?._id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        // Invalidate specific queries or query keys after a successful mutation
        queryClient.invalidateQueries(["getIngredientList"]);
      }
    }
  );
};

export const useUpdateInHouseSupllier = (opt?: MutOptions<string>) =>
  useMutation<string, ApiServiceErr, any>(async data => {
    const response = await axiosApi.put("/supplier/in-house", data);
    return response.data;
  }, opt);

export const useDeleteIngredientData = (data?: any) => {
  const queryClient = useQueryClient();
  return useMutation<string, ApiServiceErr, any>(
    async id => {
      const response = await axiosApi.delete(`ingredient/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        // Invalidate specific queries or query keys after a successful mutation
        queryClient.invalidateQueries(["getIngredientList"]);
      }
    }
  );
};

export const useGetProductIngredient = (productId: any) =>
  useQuery(
    ["getProductIngredientList", productId],
    async () => {
      const response = await axiosApi.get(`/ingredient/by-product-id/${productId}`);
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
      enabled: !!productId,
      onError: () => {
        errorView("Something went wrong");
      }
    }
  );

export const useGetIngredientPackaging = (data: any) =>
  useQuery(
    ["getIngredientPackaging", data],
    async () => {
      const response = await axiosApi.get(
        `/ingredient/packaging/${data.ingredientId}/${data.supplierId}`
      );
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
      enabled: !!data.loadPackageData,
      onError(err: any) {
        if (err.msg === "Please create packaging") {
          toast.warning("Please create packaging");
        } else {
          errorView("Something went wrong");
        }
      }
    }
  );

export const usePostCreateIngredientPackaging = (data?: any) => {
  return useMutation<string, ApiServiceErr, any>(async data => {
    const response = await axiosApi.post(
      `/ingredient/packaging/${data.packagingType}/${data.ingredientId}/${data.supplierId}`,
      data.data,
      {
        params: {
          packagingId: data?.packagingId
        }
      }
    );
    return response.data;
  });
};

export const usePutUpdateIngredientPackaging = (data?: any) => {
  return useMutation<string, ApiServiceErr, any>(async data => {
    const response = await axiosApi.put(
      `/ingredient/packaging/${data.packagingType}/${data?.packagingId}`,
      data.data,
      {
        params: {
          ingredientId: data.ingredientId
        }
      }
    );
    return response.data;
  });
};

export const usePostUpdateIngredientPackagingUnit = (data?: any) => {
  return useMutation<string, ApiServiceErr, any>(async data => {
    const response = await axiosApi.post(`/ingredient/packaging/detail`, data.data, {
      params: {
        ingredientId: data.ingredientId,
        supplierId: data.supplierId,
        packagingId: data?.packagingId
      }
    });
    return response.data;
  });
};

export const useDeleteIngredientPackaging = (data?: any) => {
  return useMutation<string, ApiServiceErr, any>(async data => {
    const response = await axiosApi.delete(
      `/ingredient/packaging/${data.packagingType}/${data.subPackagingId}`,
      {
        params: {
          ingredientId: data.ingredientId,
          packagingId: data?.packagingId
        }
      }
    );
    return response.data;
  });
};

export const useUploadIngredientImage = () =>
  useMutation<any, ApiServiceErr, any>(
    async (data: IngredientImageData) => {
      const response = await axiosApi.post(`/ingredient/upload`, data, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      return response.data;
    },
    {
      onSuccess() {
        successMessage("Ingredient image Upload successfully");
      }
    }
  );

export const useDeleteUploadIngredientImage = () =>
  useMutation<any, ApiServiceErr, any>(
    async (data: IngredientImageId) => {
      const response = await axiosApi.delete(`/ingredient/image`, {
        params: { ingredient_id: data.ingredient_id }
      });
      return response.data;
    },
    {
      onSuccess() {
        successMessage("Ingredient image delete successfully");
      }
    }
  );

export const useCheckIngredientInMapping = () =>
  useMutation<boolean, ApiServiceErr, CheckDrawingData>(async data => {
    const response = await axiosApi.post(`/ingredient/drawing-check`, data);
    return response.data.exists;
  });

export const useDeleteIngredient = () =>
  useMutation<any, ApiServiceErr, any>(
    async data => {
      const response = await axiosApi.delete(`/ingredient`, { data });
      return response.data;
    },
    {
      onSuccess: () => {
        successMessage("Ingredient deleted successfully");
      }
    }
  );

export const useGetProductsByIngredient = (data: any) =>
  useQuery(
    ["getProductByIngredient", data],
    async () => {
      const response = await axiosApi.get(
        `/ingredient/products/${data.ingredientId}/${data.supplierId}`
      );
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
      enabled: !!data.ingredientId && !!data.supplierId,
      onError: () => {
        errorView("Something went wrong");
      }
    }
  );

export const useGetInHouseIngredient = () =>
  useQuery<any, ApiServiceErr>(
    ["useGetInHouseIngredient"],
    async () => {
      const response = await axiosApi.get(`/ingredient/in-house-ingredient`);

      let inHouseIngredients = response?.data.map((data: any) => {
        let inHouseIngredient = {
          key: data?.id,
          disabled: data?.isDisable,
          text: data?.ingredient_name,
          value: data?.id
        };
        return inHouseIngredient;
      });
      return inHouseIngredients;
    },
    {
      cacheTime: 0
    }
  );

export const useGetReviewData = (data: { drawingId: string | undefined }) =>
  useQuery<ApiProductReviewResponse, ApiServiceErr>(
    ["useGetIngredientReviewData", data],
    async () => {
      const response = await axiosApi.get(
        `/drawing-tool/product/mapping-all-data/${data.drawingId}`
      );
      return response.data;
    },
    {
      cacheTime: 0
    }
  );

export const useGetFarmGateData = (data: any) =>
  useQuery<any, ApiServiceErr>(
    ["useGetFarmGateDetails", data],
    async () => {
      const response = await axiosApi.get(`/farm-gate/${data.farmGateId}`);
      return response.data;
    },
    {
      cacheTime: 0,
      enabled: !!data.farmGateId
    }
  );

export const useGetProcessGateData = (data: any) =>
  useQuery<any, ApiServiceErr>(
    ["useGetProcessDetails", data],
    async () => {
      const response = await axiosApi.get(`/process-gate/${data.processId}`);
      return response.data;
    },
    {
      cacheTime: 0,
      enabled: !!data.processId
    }
  );

export const useGetSupplierIngredients = (params: SupplierIngredientsParams) =>
  useQuery<any, ApiServiceErr>(
    ["useGetSupplierIngredients", params.sourceId, params.ownerId],
    async () => {
      const response = await axiosApi.get(`/ingredient/supplier-ingredients`, {
        params: {
          sourceId: params.sourceId,
          ownerId: params.ownerId
        }
      });
      return response.data.map((ingred: any) => ({
        id: ingred._id,
        title: ingred.ingredient_name,
        ingredientData: ingred
      }));
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
      enabled: !!params.sourceId && !!params.ownerId,
      onError: () => {
        errorView("Something went wrong");
      }
    }
  );

export const useGetSalesUnitByIngredient = (data: any) => {
  return useQuery(
    ["getSalesUnitByIngredient", data.ingredientId, data.supplierId],
    async () => {
      const response = await axiosApi.get(
        `/ingredient/last-supplier-ingredient-detail/${data.ingredientId}/${data.supplierId}`
      );

      return response.data;
    },
    {
      staleTime: 0,  // Data is always considered stale
      cacheTime: 0,  // Don't cache the data
      refetchOnWindowFocus: true,  // Refetch when window focuses
      refetchOnMount: "always",  // Always refetch when component mounts
      enabled: !!data.ingredientId && !!data.supplierId,
      onError: error => {
        errorView("Something went wrong");
      }
    }
  );
};

export const useUploadSupportingDocument = (options?: MutOptions<any>) =>
  useMutation(
    async (params: SupportingDocumentParams) => {
      const formData = new FormData();
      formData.append("ingredientId", params.ingredientId);
      formData.append("supplierId", params.supplierId);
      formData.append("file", params.file);

      const response = await axiosApi.post("/supplier/supporting-documents", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "*/*"
        }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        successMessage("Document uploaded successfully");
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.message || error?.message || "Failed to upload document";
        errorView(message);
      },
      ...options
    }
  );
